import { getPaymentMethodDisplayName } from "helpers/getPaymentDisplayName"

export interface topList {
  topList: topListBuckets
}

export interface topListBuckets {
  buckets: bucket[]
}

export interface bucket {
  topProductDetails?: {
    hits: {
      hits: {
        _source: topProductDetailsData
      }[]
    }
  }
  totalQuantity: value
  discountTotal?: value
  priceTotal?: value
  totalValuesPerCurrencyCode: {
    buckets: {
      doc_count: number
      key: string
      totalPriceAmount: value
      totalTaxAmount: value
      totalExcludingTax: value
      totalIncludingTax: value
    }[]
  }
  ordersTopValues: {
    topCountries: {
      buckets: {
        doc_count: number
        key: string
      }[]
    }
    topStores: {
      buckets: {
        doc_count: number
        key: string
      }[]
    }
  }
}

export interface topDiscountCodes {
  buckets: topDiscountData[]
}

export interface topDiscountData {
  doc_count: number
  key: string
  subTotal: value
  discountTotal: value
  byCountry: byValue
  byStoreGroupId: byValue
}

export interface topDiscountRulesData {
  doc_count: number
  key: string
  name?: string
  subTotal: value
  discountTotal: value
  byCountry: byValue
  byStoreGroupId: byValue
}

export interface nestedDiscountCodeRuleData {
  doc_count: number
  key: string
  order_level?: {
    doc_count: number
    unique_orders: {
      value: number
    }
    subTotal: value
    discountTotal: value
    byCountry: byValue
    byStoreGroupId: byValue
  }
}

export interface byValue {
  buckets: {
    key: string
    doc_count: number
  }[]
}

export interface topProductDetailsData {
  productVariantId: string
  productParentId: string
  name: string
  quantity: number
  imageUrl: string
  distributedTotalPriceAmount: number
  campaign?: { name: string }
}

export interface addData {
  totalValuesPerCurrencyCode: buckets
  totalValuesPerStoreGroup: buckets
  totalByPaymentMethod: buckets
  totalByPaymentProvider: buckets
  topProductVariants: topList
  topProductVariantsFilterByCampaign: {
    filtered_orderLines: topList
  }
  topProductVariantsFilterByNonDiscount: {
    filtered_orderLines: topList
  }
  topProductParents: topList
  topProductParentsFilterByCampaign: {
    filtered_orderLines: topList
  }
  topProductParentsFilterByNonDiscount: {
    filtered_orderLines: topList
  }
  topDiscountCodes: topDiscountCodes
  topDiscountCodeRules: {
    ruleIds: {
      buckets: nestedDiscountCodeRuleData[]
    }
  }
  topDiscountExternalRules: {
    ruleIds: {
      buckets: nestedDiscountCodeRuleData[]
    }
  }
}

export interface buckets {
  buckets: totalValue[]
}

export interface totalValue {
  key: string
  doc_count: number
  totalExcludingTax: value
  totalIncludingTax: value
}

export interface value {
  value: number
}

export function groupDiscountCodesByUppercaseKey(data: topDiscountData[]): topDiscountData[] {
  // Create a map to group by uppercase keys
  const groupedMap = new Map<string, topDiscountData>()

  // Process each item
  data.forEach((item) => {
    const uppercaseKey = item.key.toUpperCase()

    if (groupedMap.has(uppercaseKey)) {
      // If this uppercase key already exists, merge the data
      const existingItem = groupedMap.get(uppercaseKey)!

      // Update counts and totals
      existingItem.doc_count += item.doc_count
      existingItem.subTotal.value += item.subTotal.value
      existingItem.discountTotal.value += item.discountTotal.value
      existingItem.byCountry = mergeByValue(existingItem.byCountry, item.byCountry)
      existingItem.byStoreGroupId = mergeByValue(existingItem.byStoreGroupId, item.byStoreGroupId)
    } else {
      // Create a new entry with the uppercase key
      groupedMap.set(uppercaseKey, {
        ...item,
        key: uppercaseKey
      })
    }
  })

  // Convert map back to array
  return Array.from(groupedMap.values())
}

function mergeByValue(existing: byValue, newItem: byValue): byValue {
  const countryMap = new Map<string, number>()

  // Process existing countries
  if (existing?.buckets) {
    existing.buckets.forEach((bucket) => {
      countryMap.set(bucket.key, (countryMap.get(bucket.key) || 0) + bucket.doc_count)
    })
  }

  // Process new countries
  if (newItem?.buckets) {
    newItem.buckets.forEach((bucket) => {
      countryMap.set(bucket.key, (countryMap.get(bucket.key) || 0) + bucket.doc_count)
    })
  }

  // Convert to byCountry object with sorted buckets
  return {
    buckets: Array.from(countryMap.entries())
      .map(([key, doc_count]) => ({
        key,
        doc_count
      }))
      .sort((a, b) => b.doc_count - a.doc_count)
  }
}

export function groupByPaymentMethodName(data: totalValue[]) {
  const groupedByName = data.reduce((result: { [key: string]: number }, x) => {
    const name = getPaymentMethodDisplayName(x.key)
    if (result[name]) {
      result[name] += x.doc_count
    } else {
      result[name] = x.doc_count
    }
    return result
  }, {})

  return Object.entries(groupedByName || {})
    .map(([name, value]) => ({
      name,
      value
    }))
    .sort((a, b) => b.value - a.value)
}
